"use client";

import { Dialog, Button, Link } from "@marketing-sites/ui/primitives";
import { cn } from "@marketing-sites/utils/cn";
import { Popup } from "../popup";

export interface FreeSeatsCardMetadata {
  analyticsId: string;
  overlayMetadata: { analyticsId: string };
  buttonMetadata: { analyticsId: string };
}

interface FreeSeatsCard {
  title: string;
  description: string;
  cta: string;
  href: string;
}

function CornerDots({ className }: { className?: string }) {
  return (
    <>
      {(
        [
          "top-0 left-0",
          "top-0 right-0",
          "bottom-0 left-0",
          "bottom-0 right-0",
        ] as const
      ).map((pos) => (
        <span
          key={pos}
          aria-hidden
          className={cn("absolute size-1 transition-colors", pos, className)}
        />
      ))}
    </>
  );
}

function FrameDot() {
  return <span aria-hidden className="bg-clay size-2" />;
}

export interface FreeSeatsProps {
  trialHref: string;
  salesHref: string;
  modalMetadata: { analyticsId: string };
  trialMetadata: FreeSeatsCardMetadata;
  salesMetadata: FreeSeatsCardMetadata;
}

export default function FreeSeats({
  trialHref,
  salesHref,
  modalMetadata,
  trialMetadata,
  salesMetadata,
}: FreeSeatsProps) {
  const cards: FreeSeatsCard[] = [
    {
      title: "Start free trial",
      description:
        "The fastest way to get started. Your free seats are applied when you sign up.",
      cta: "Start free trial",
      href: trialHref,
    },
    {
      title: "Talk to our sales team",
      description:
        "Got questions or a larger team to migrate? We'll walk you through everything.",
      cta: "Contact sales",
      href: salesHref,
    },
  ];

  return (
    <Popup closeButton={null} className="md:w-[min(62rem,calc(100vw-2rem))]">
      <div
        className="-mx-4 flex flex-col"
        data-analytics-region="free-seats-modal"
        data-analytics-id={modalMetadata.analyticsId}
        style={
          {
            "--button-primary-hover-background": "var(--color-fin)",
            "--button-primary-active-background":
              "color-mix(in oklab, var(--color-fin) 85%, transparent)",
          } as React.CSSProperties
        }
      >
        {/* Header bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4">
          <FrameDot />
          <Dialog.Close
            aria-label="Close"
            className="flex cursor-pointer items-center gap-2"
          >
            <span className="text-inline-link text-button-sm font-sans">
              Close
            </span>
            <FrameDot />
          </Dialog.Close>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col">
          {/* Heading */}
          <div className="px-4 pb-10 pt-14 text-center md:px-12 md:pb-16 md:pt-20">
            <Dialog.Title className="text-heading-3 font-sans">
              <span className="text-off-black/60">How do you want to </span>
              <span className="text-off-black">get started?</span>
            </Dialog.Title>
          </div>

          {/* Cards */}
          <div className="flex flex-col gap-6 px-4 pb-14 md:flex-row md:gap-10 md:px-14">
            {cards.map((card, index) => (
              <div
                key={card.title}
                data-analytics-region={`free-seats-card-${index === 0 ? "trial" : "sales"}`}
                data-analytics-id={
                  index === 0
                    ? trialMetadata.analyticsId
                    : salesMetadata.analyticsId
                }
                className={cn(
                  "group relative flex flex-1 flex-col items-center justify-center p-10",
                  "bg-chalk hover:bg-canvas h-[250px] transition-colors",
                  "hover:[--button-primary-background:var(--color-fin)]",
                  "hover:[--button-primary-hover-background:var(--color-fin)]",
                )}
              >
                <Link
                  analyticsId={
                    index === 0
                      ? trialMetadata.overlayMetadata.analyticsId
                      : salesMetadata.overlayMetadata.analyticsId
                  }
                  href={card.href}
                  className="absolute inset-0 z-10"
                  aria-label={card.cta}
                />
                <CornerDots className="bg-off-black group-hover:bg-fin" />
                <div className="flex flex-col items-center gap-8">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <h3 className="text-heading-5-desktop text-off-black font-sans">
                      {card.title}
                    </h3>
                    <p className="text-body text-off-black/60 font-sans">
                      {card.description}
                    </p>
                  </div>
                  <Button
                    analyticsId={
                      index === 0
                        ? trialMetadata.buttonMetadata.analyticsId
                        : salesMetadata.buttonMetadata.analyticsId
                    }
                    tabIndex={-1}
                    aria-hidden
                  >
                    {card.cta}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer bar */}
        <div className="flex items-center justify-between px-4">
          <FrameDot />
          <FrameDot />
        </div>
      </div>
    </Popup>
  );
}
